import {
  useCallback, useMemo, useEffect, useRef,
} from 'react'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import _get from 'lodash/get'
import type { Dispatch, UnknownAction } from 'redux'
import useLatest from '../../shared/use-latest'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../apollo-graphql/index'
import {
  GET_MY_PLAYLISTS_SHELL,
  UPDATE_PLAYLIST_VISIBILITY,
  DELETE_PLAYLIST,
  ADD_PLAYLIST_ITEM,
  REMOVE_PLAYLIST_ITEM,
  UPDATE_PLAYLIST_ITEM,
} from '../../query'
import { CREATE_PLAYLIST, FLAG_WATCHED_VIDEO } from '../../../playlist/query'
import copyTextToClipboard from '../../shared/clipboard-utils'
import showSaveErrorToast from '../../shared/save-error-toast'
import getText from '../../shared/static-text-utils'
import { addToasty } from '../../../toasty/actions'
import { resolveNonCollidingPlaylistName } from '../../shared/playlist-name-utils'
import nextAvailableCopyName from '../../shared/playlist-suffix-walk'
import useReservedSystemPlaylistNames from '../../shared/useReservedSystemPlaylistNames'
import { renamePlaylist as renamePlaylistApi } from '../../../playlist/index'
import {
  filterUserFacingPlaylists,
} from './content-utils'
import useApolloListPagination from '../../shared/useApolloListPagination'
import { createRemoveItemUpdater } from '../../shared/cache-updaters'
import type {
  GetMyPlaylistsShellResponse,
  PlaylistShell,
  PlaylistVisibilityLevel,
  EnrichedPlaylistItem,
  EnrichedInProgressItem,
  StaticTextMap,
} from '../../types'

// Module-scope empty sentinel so every empty-case memo returns the same
// reference.  Prevents downstream re-renders.
const EMPTY_PLAYLISTS: PlaylistShell[] = []

interface UseProfilePlaylistsParams {
  /**
   * Viewer uuid — used for targeted cache updates (e.g. hiding an in-progress
   * tile).  Must be the `uuid` value because the Apollo cache normalizes
   * `UserProfile` entities by `uuid` (see `apollo-graphql/index.js` —
   * `UserProfile: { keyFields: ['uuid'] }`).  Passing `uid` would make
   * `cache.identify` return `undefined` and `cache.modify` a silent no-op.
   */
  uuid?: string
  language: string
  jwt: string
  isLoggedIn: boolean
  dispatch: Dispatch<UnknownAction>
  staticText: StaticTextMap
  history?: { push: (path: string) => void }
  /** When false, skips GET_MY_PLAYLISTS (e.g. community feature gate will 404). */
  queriesEnabled?: boolean
}

export default function useProfilePlaylists (params: UseProfilePlaylistsParams) {
  // `language` and `history` stay on the param interface so callers continue
  // to thread them through (useCollectionsData), but they're no longer
  // consumed inside this hook — per-row hydration owns the GET_CONTENT call
  // site, and the shuffle case (only history consumer) moved to the row.
  const {
    uuid, jwt, isLoggedIn, dispatch, staticText, queriesEnabled = true,
  } = params
  const apolloClient = useApolloClient()

  // cache-and-network: user-scoped shell — playlistItems carry only IDs.
  // Per-row hydration via useRowHydration owns the Content fetch.
  //
  // The cache paint lets a return visit to /playlist/default render the last
  // playlist set instantly (no spinner) while a background refetch validates
  // against the server.  Safety:
  //   • Mutations keep the cache correct.  REMOVE_PLAYLIST_ITEM and
  //     FLAG_WATCHED_VIDEO below use cache.modify; CREATE / DELETE / RENAME
  //     still call refetchRef.current() which now runs against a primed cache
  //     instead of an empty one.
  //   • User-data leak across logout / profile-switch is not a concern —
  //     both flows call global.location.assign() (see auth/actions.js
  //     doAuthLogout + changeAuthProfile), which wipes the in-memory cache.
  const SHELL_PAGE_SIZE = 24

  const {
    data: playlistsRawData,
    loading: shellLoading,
    refetch: refetchPlaylists,
    fetchMore: fetchMoreShell,
  } = useQuery<GetMyPlaylistsShellResponse>(GET_MY_PLAYLISTS_SHELL, {
    variables: { limit: SHELL_PAGE_SIZE, offset: 0 },
    skip: !isLoggedIn || !queriesEnabled,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  // Route `refetchPlaylists` through a stable ref. Apollo does not
  // guarantee a stable identity for `refetch` (it rebuilds on variable /
  // load-state transitions), so listing it directly in callback deps
  // would force every handler that calls it to re-create its closure
  // on every Apollo update — which propagates through `useCollectionsData`
  // and cascades into row re-renders. `useLatest` gives us a ref whose
  // `.current` is always the latest refetch and whose identity never
  // changes, safe for both callback bodies (via `.current()`) and the
  // dep arrays of the two listener effects defined later in this hook.
  // Hoisted here from below the action handlers (was at ~L529) so the
  // five mutation handlers can also read it.
  const refetchRef = useLatest(refetchPlaylists)

  // Offset-based pagination via the shared adaptor. Mirrors the same shape
  // used by useWatchHistoryData (offset) + useFollowedPlaylists (cursor).
  const { loadMore, isLoadingMore } = useApolloListPagination<GetMyPlaylistsShellResponse>(
    fetchMoreShell as unknown as Parameters<typeof useApolloListPagination>[0],
    playlistsRawData,
    { type: 'offset', listPath: 'playlistList.playlists', pageSize: SHELL_PAGE_SIZE },
    { limit: SHELL_PAGE_SIZE },
  )

  const [updatePlaylistVisibility] = useMutation(UPDATE_PLAYLIST_VISIBILITY, {
    context: { jwt },
    // Apollo auto-normalizes the returned `{ playlist { id, visibility } }`
    // into `Playlist:<id>`, which is usually enough.  But the library page's
    // GET_MY_PLAYLISTS query has historically run with network-only — a
    // navigation there could overwrite the just-updated cache entity with a
    // stale server response.  The explicit cache.modify pins the value so
    // even a subsequent network response that omits or contradicts it
    // doesn't clobber what the user just chose.  Belt-and-suspenders paired
    // with SystemPlaylistFullView's fetchPolicy flip to cache-and-network.
    update: (cache, { data }) => {
      const updated = data?.updatePlaylistVisibility?.playlist
      if (!updated?.id) return
      const id = cache.identify({ __typename: 'Playlist', id: updated.id })
      if (!id) return
      cache.modify({
        id,
        fields: { visibility: () => updated.visibility },
      })
    },
  })
  const [deletePlaylistMutation] = useMutation(DELETE_PLAYLIST, {
    context: { jwt },
  })
  const [createPlaylistMutation] = useMutation(CREATE_PLAYLIST, {
    context: { jwt },
  })
  const [addPlaylistItemMutation] = useMutation(ADD_PLAYLIST_ITEM, {
    context: { jwt },
  })
  const [removePlaylistItemMutation] = useMutation(REMOVE_PLAYLIST_ITEM, {
    context: { jwt },
  })
  const [updatePlaylistItemMutation] = useMutation(UPDATE_PLAYLIST_ITEM, {
    context: { jwt },
  })
  const [flagWatchedVideoMutation] = useMutation(FLAG_WATCHED_VIDEO, {
    context: { jwt },
  })

  const allRawPlaylists = useMemo<PlaylistShell[]>(
    () => (
      _get(playlistsRawData, 'playlistList.playlists', EMPTY_PLAYLISTS) || EMPTY_PLAYLISTS
    ),
    [playlistsRawData],
  )

  // useLatest-backed ref so `handlePlaylistAction` (delete / rename /
  // duplicate flows below) can read the LATEST playlist array at call-time
  // without listing `allRawPlaylists` in its dep array. Without this,
  // `allRawPlaylists` rebuilds on every Apollo broadcast (its memo dep is
  // `playlistsRawData` — recreated by Apollo on every cache notification
  // even when contents are structurally identical), forcing the handler to
  // re-create and breaking downstream React.memo compares.
  const allRawPlaylistsRef = useLatest(allRawPlaylists)

  const allUserPlaylistsJS = useMemo(
    // `filterUserFacingPlaylists` keys off `id` + `playlistType` only, both
    // of which the shell carries — safe to call with the shell shape.
    () => filterUserFacingPlaylists(
      allRawPlaylists as unknown as Parameters<typeof filterUserFacingPlaylists>[0],
    ),
    [allRawPlaylists],
  )

  const { defaultPlaylist, favoritesPlaylist } = useMemo(() => {
    const result: {
      defaultPlaylist: PlaylistShell | null
      favoritesPlaylist: PlaylistShell | null
    } = {
      defaultPlaylist: null,
      favoritesPlaylist: null,
    }
    allRawPlaylists.some((pl: PlaylistShell) => {
      if (!pl) return false
      if (pl.playlistType === 'default') result.defaultPlaylist = pl
      else if (pl.playlistType === 'favorites') result.favoritesPlaylist = pl
      return !!(result.defaultPlaylist && result.favoritesPlaylist)
    })
    return result
  }, [allRawPlaylists])

  // useLatest-backed refs so the four system-playlist item handlers
  // (handleRemove* / handleReorder* for WatchLater + Favorites) can read
  // the LATEST playlist at click-time without listing the playlist object
  // in their dep arrays. The playlist memo above rebuilds on every Apollo
  // broadcast (its dep is `allRawPlaylists`); routing through refs keeps
  // the handler identities stable so React.memo'd consumers stay memoized
  // across cache notifications.
  const defaultPlaylistRef = useLatest(defaultPlaylist)
  const favoritesPlaylistRef = useLatest(favoritesPlaylist)

  // Per-row hydration is owned by each variant via useRowHydration —
  // there is no page-level Content fetch. Rows do not share a hydration
  // map, so a cache.modify on one row doesn't cascade into siblings.

  // Visibility of the Watch Later (default) system playlist. Renamed from
  // `currentlyWatchingVisibility` to avoid colliding with the new
  // `UserProfile.currentlyWatchingVisibility` FieldVisibility flag (which
  // gates the In Progress activity surface, not Watch Later).
  const watchLaterVisibility = defaultPlaylist
    ? (defaultPlaylist.visibility || 'PRIVATE')
    : 'PRIVATE'

  // Extract primitives from `staticText` so downstream callbacks can depend
  // on strings (byte-equal on re-creation) instead of the whole Immutable
  // Map (identity-different on every parent render).  This keeps
  // `errorToast`, `handlePlaylistAction`, and the four downstream handlers
  // that close over `errorToast` identity-stable when only the Map
  // identity — not its content — changes.
  const saveErrorMessage = getText(staticText, 'saveError', '')
  const copySuffix = getText(staticText, 'copySuffix', 'Copy')
  // Template for the auto-rename info toast — extracted as a primitive so
  // `handlePlaylistAction`'s dep array doesn't churn on the whole `staticText`
  // Immutable Map.
  const autoRenamedTemplate = getText(
    staticText,
    'autoRenamedNotice',
    'Saved as "{finalName}" — that name is already taken or reserved.',
  )

  const errorToast = useCallback(() => {
    showSaveErrorToast(dispatch, saveErrorMessage)
  }, [dispatch, saveErrorMessage])

  // Locale-aware reserved system playlist names ("Watch Later", "Favorites",
  // "Currently Watching", "Watch History"). Used by the rename path's
  // collision resolver below.
  const reservedNames = useReservedSystemPlaylistNames(staticText)

  // Fires the auto-rename info toast when the rename collision walk
  // produced a name different from the user's typed text.
  const fireAutoRenamedToast = useCallback((originalName: string, finalName: string) => {
    const message = autoRenamedTemplate
      .replace('{finalName}', finalName)
      .replace('{originalName}', originalName)
    dispatch(addToasty(message))
  }, [dispatch, autoRenamedTemplate])

  const handleUpdatePlaylistVisibility = useCallback(async (
    playlistId: string,
    visibility: PlaylistVisibilityLevel,
  ) => {
    try {
      await updatePlaylistVisibility({ variables: { playlistId, visibility } })
      // UPDATE_PLAYLIST_VISIBILITY returns `{ playlist { id, visibility } }`,
      // which Apollo auto-normalizes into the `Playlist:<id>` cache entity.
      // Every component reading `visibility` from that entity re-renders
      // from cache without a network roundtrip, so no refetch is needed.
    } catch {
      errorToast()
    }
  }, [updatePlaylistVisibility, errorToast])

  const handleRemovePlaylistItem = useCallback(async (
    playlistId: string,
    playlistType: string,
    item: EnrichedPlaylistItem,
  ) => {
    // Use the playlist-item ROW's own contentId / contentType so the backend
    // can locate the row. Reading from the tile fails for series-pivoted
    // tiles (`'series'`) and specialized content types like `'gaiaOriginal'`,
    // neither of which matches the row's stored `content_type`.
    const contentId = item.playlistItemContentId
    const contentType = item.playlistItemContentType || 'video'
    if (!contentId) return
    const playlistItemId = item.id
    try {
      await removePlaylistItemMutation({
        variables: {
          contentId, contentType, playlistId, playlistType,
        },
        update (cache) {
          // Surgical cache update via the shared factory. Drops the removed
          // playlistItem from the cached Reference[] and decrements the
          // sibling itemCount in the same modify() pass — keeps the (count,
          // array) pair consistent on the next read.
          const updater = createRemoveItemUpdater({
            entityId: cache.identify({ __typename: 'Playlist', id: playlistId }) || undefined,
            field: 'playlistItems',
            countField: 'itemCount',
            countLocation: 'sibling',
            match: (ref, readField) => String(readField<string>('id', ref)) === String(playlistItemId),
          })
          updater(cache)
        },
      })
    } catch {
      errorToast()
    }
  }, [removePlaylistItemMutation, errorToast])

  // Trailing-edge debounce: rapid Move-Earlier / Move-Later clicks
  // would otherwise queue one full GET_MY_PLAYLISTS round-trip per
  // click. Optimistic local state already paints the new order; the
  // refetch is purely server reconciliation.
  const reorderRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reorderRefetchPlaylistsRef = useLatest(refetchPlaylists)
  const scheduleReorderRefetch = useCallback(() => {
    if (reorderRefetchTimerRef.current) {
      clearTimeout(reorderRefetchTimerRef.current)
    }
    reorderRefetchTimerRef.current = setTimeout(() => {
      reorderRefetchTimerRef.current = null
      reorderRefetchPlaylistsRef.current()
    }, 250)
  }, [reorderRefetchPlaylistsRef])

  useEffect(() => () => {
    if (reorderRefetchTimerRef.current) {
      clearTimeout(reorderRefetchTimerRef.current)
    }
  }, [])

  /**
   * Reorder a playlist item via `playlistUpdatePlaylistItem`.  Wired into
   * `<PlaylistReorderControls>` Move Earlier / Move Later buttons.
   *
   * `newPosition` is the destination row's current `playlistPosition` — the
   * server interprets this as "place the moved item at this slot" and
   * (with `reorderPlaylist: true`, the schema default) renormalizes the
   * other rows so positions stay dense.  The Brooklyn resolver requires
   * the row's `contentId` and `contentType` even though the schema marks
   * them nullable — same pattern as `playlistDeletePlaylistItem`.
   */
  const handleReorderPlaylistItem = useCallback(async (
    playlistId: string,
    playlistType: string,
    item: EnrichedPlaylistItem,
    newPosition: number,
  ) => {
    const itemId = item?.id
    const contentId = item?.playlistItemContentId
    const contentType = item?.playlistItemContentType || 'video'
    if (!itemId || !contentId) return
    try {
      await updatePlaylistItemMutation({
        variables: {
          playlistItemId: itemId,
          contentId,
          contentType,
          playlistPosition: newPosition,
          playlistId,
          playlistType,
        },
      })
      scheduleReorderRefetch()
    } catch {
      errorToast()
    }
  }, [updatePlaylistItemMutation, scheduleReorderRefetch, errorToast])

  const handlePlaylistAction = useCallback(async (
    playlistId: string,
    action: string,
    playlistType?: string,
    playlistName?: string,
  ) => {
    // Read the latest playlist list + refetch fn at call-time via
    // useLatest-backed refs, so this callback's identity is stable across
    // Apollo broadcasts that would otherwise flip `allRawPlaylists` and
    // `refetchPlaylists`. Semantically equivalent — the handler always
    // operated on "current" state at click-time, and Apollo's network-only
    // policy means there's no stale-closure window between click and submit
    // that matters for the mutation flows below.
    const allRawPlaylistsNow = allRawPlaylistsRef.current
    switch (action) {
      // 'shuffle' is intentionally absent — shuffle lives on the row because
      // the row already has hydrated items locally via useRowHydration.
      case 'delete':
        if (playlistType) {
          try {
            await deletePlaylistMutation({ variables: { playlistType } })
            refetchRef.current()
          } catch {
            errorToast()
          }
        }
        break
      case 'edit':
      case 'rename':
        if (playlistType && playlistName) {
          // Resolve against reserved system names + user's other playlists.
          // Exclude the playlist being renamed so a no-op rename (typing
          // the same name back) doesn't false-positive into "<name> 2".
          const existing = allRawPlaylistsNow
            .filter((p) => p?.id !== playlistId)
            .map((p) => p?.playlistName)
            .filter((n): n is string => Boolean(n))
          const finalName = resolveNonCollidingPlaylistName(playlistName, reservedNames, existing)
          try {
            await renamePlaylistApi({
              auth: { jwt }, playlistType, playlistName: finalName,
            })
            if (finalName !== playlistName) fireAutoRenamedToast(playlistName, finalName)
            refetchRef.current()
          } catch {
            errorToast()
          }
        }
        break
      case 'copyToLibrary':
      case 'duplicate': {
        if (!playlistName) break
        // Walk past existing "X - Copy", "X - Copy 2", … so a repeat-
        // duplicate produces a fresh playlist instead of silently merging
        // items into an existing copy (today's backend dedupe behavior;
        // also future-proofs against an upcoming backend change that will
        // throw on duplicate names instead of dedupe).
        const copyExistingNames = allRawPlaylistsNow
          .map((p) => p?.playlistName)
          .filter((n): n is string => Boolean(n))
        const copyName = nextAvailableCopyName(playlistName, copySuffix, copyExistingNames)
        try {
          const { data: createData } = await createPlaylistMutation({
            variables: { playlistName: copyName },
          })
          const newPlaylist = _get(createData, 'playlistCreate.playlist')
          if (newPlaylist) {
            // Shell-driven duplication: iterate the source playlist's shell
            // items directly and re-issue addPlaylistItem with the row's
            // contentId / contentType. String coercion + `||`-on-contentType
            // matches the content-utils convention (it?.contentType ||
            // 'video') — rejects '' and null alike.
            const sourceShell = allRawPlaylistsNow.find((p) => p?.id === playlistId)
            const sourceShellItems = sourceShell?.playlistItems ?? []
            await Promise.all(
              sourceShellItems.flatMap((it) => {
                const rawCid = it?.contentId
                const cid = rawCid == null || rawCid === '' ? null : Number(rawCid)
                if (cid == null || !Number.isFinite(cid)) return []
                return [addPlaylistItemMutation({
                  variables: {
                    contentId: cid,
                    contentType: it?.contentType || 'video',
                    playlistType: newPlaylist.playlistType,
                    playlistId: newPlaylist.id,
                  },
                })]
              }),
            )
            refetchRef.current()
          }
        } catch {
          errorToast()
        }
        break
      }
      case 'share': {
        // Handled by caller via setSharePlaylistId — no-op here
        break
      }
      case 'copyLink': {
        if (typeof window !== 'undefined') {
          const url = `${window.location.origin}/playlist/shared/${playlistId}`
          try {
            await copyTextToClipboard(url)
          } catch {
            errorToast()
          }
        }
        break
      }
      default:
        break
    }
  }, [
    deletePlaylistMutation, createPlaylistMutation, addPlaylistItemMutation,
    jwt, errorToast, copySuffix,
    // allRawPlaylists + refetchPlaylists removed — read via useLatest refs above.
    allRawPlaylistsRef, refetchRef,
    reservedNames, fireAutoRenamedToast,
  ])

  // Read defaultPlaylist / favoritesPlaylist via useLatest refs (defined
  // above) so these four handlers stay referentially stable across Apollo
  // broadcasts. Semantically identical — the handler reads the latest
  // playlist at click-time, which is what every flow already expects (the
  // playlist memo on `allRawPlaylists` is the source of truth).
  const handleRemoveWatchLaterItem = useCallback((item: EnrichedPlaylistItem) => {
    const pl = defaultPlaylistRef.current
    if (pl) {
      handleRemovePlaylistItem(pl.id, 'default', item)
    }
  }, [defaultPlaylistRef, handleRemovePlaylistItem])

  const handleRemoveFavoritesItem = useCallback((item: EnrichedPlaylistItem) => {
    const pl = favoritesPlaylistRef.current
    if (pl) {
      handleRemovePlaylistItem(pl.id, 'favorites', item)
    }
  }, [favoritesPlaylistRef, handleRemovePlaylistItem])

  const handleReorderWatchLaterItem = useCallback((
    item: EnrichedPlaylistItem,
    newPosition: number,
  ) => {
    const pl = defaultPlaylistRef.current
    if (pl) {
      handleReorderPlaylistItem(pl.id, 'default', item, newPosition)
    }
  }, [defaultPlaylistRef, handleReorderPlaylistItem])

  const handleReorderFavoritesItem = useCallback((
    item: EnrichedPlaylistItem,
    newPosition: number,
  ) => {
    const pl = favoritesPlaylistRef.current
    if (pl) {
      handleReorderPlaylistItem(pl.id, 'favorites', item, newPosition)
    }
  }, [favoritesPlaylistRef, handleReorderPlaylistItem])

  const handleHideInProgressItem = useCallback(async (
    _playlistId: string,
    _playlistType: string,
    item: EnrichedPlaylistItem,
  ) => {
    // Bundle fan-out — mirrors SystemPlaylistFullView.handleRemoveInProgressItem.
    // A series-pivoted Currently Watching tile collapses N raw in-progress
    // rows into one visible tile (`useInProgressEnrichment.ts:97-207`); the
    // Remove click must hide every bundled episode so the series doesn't
    // re-pivot from leftover siblings on the next render.  The incoming
    // `item` is typed as EnrichedPlaylistItem for signature compatibility,
    // but in practice the in-progress carousel feeds EnrichedInProgressItem
    // shapes — narrowing-cast lets us read the bundle arrays added there.
    const inProgress = item as unknown as EnrichedInProgressItem
    const fallbackContentId = Number(item.contentId)
    const enrichedContentIds = inProgress.bundledEpisodeContentIds
    let bundleContentIds: number[]
    if (enrichedContentIds && enrichedContentIds.length > 0) {
      bundleContentIds = enrichedContentIds.filter((id) => Number.isFinite(id) && id > 0)
    } else if (Number.isFinite(fallbackContentId) && fallbackContentId > 0) {
      bundleContentIds = [fallbackContentId]
    } else {
      bundleContentIds = []
    }
    const fallbackMediaId = item.mediaId ?? item.id
    const enrichedMediaIds = inProgress.bundledEpisodeMediaIds
    let bundleMediaIds: string[]
    if (enrichedMediaIds && enrichedMediaIds.length > 0) {
      bundleMediaIds = enrichedMediaIds
    } else if (fallbackMediaId != null) {
      bundleMediaIds = [String(fallbackMediaId)]
    } else {
      bundleMediaIds = []
    }
    if (bundleContentIds.length === 0) return

    // Fan out flagWatchedVideo per bundled episode.  allSettled so a single
    // resolver failure on a tail episode doesn't block the rest from being
    // hidden — only an all-rejected outcome falls back to the error toast,
    // matching the prior single-mutation fail-loud behavior.
    const results = await Promise.allSettled(
      bundleContentIds.map((videoId) => flagWatchedVideoMutation({
        variables: { videoId, hiddenByUser: true },
      })),
    )
    const succeeded = results.filter((r) => r.status === 'fulfilled').length
    if (succeeded === 0) {
      errorToast()
      return
    }
    // Drop every bundled mediaId from the cached `UserProfile.inProgress`
    // in a single modify() pass — `useInProgressEnrichment` already filters
    // hidden entries on next render, so this is consistent with a refetch
    // without the large round-trip.
    if (uuid && bundleMediaIds.length > 0) {
      const bundleMediaIdSet = new Set(bundleMediaIds.map(String))
      apolloClient.cache.modify({
        id: apolloClient.cache.identify({ __typename: 'UserProfile', uuid }),
        fields: {
          inProgress (existing, { readField }) {
            if (!Array.isArray(existing)) return existing
            return existing.filter(
              (entry) => !bundleMediaIdSet.has(String(readField('mediaId', entry))),
            )
          },
        },
      })
    }
  }, [flagWatchedVideoMutation, apolloClient, errorToast, uuid])

  // `refetchRef` is hoisted to the top of the hook (~L119) so the five
  // mutation handlers above can also read it; the two listener effects
  // below continue to use the same ref.

  // Listen for external playlist mutations (e.g. add-to-playlist modal from
  // shared PlaylistAddRemoveGraphql).  That modal can't call our refetch
  // directly because it's outside community scope, so we bridge via a
  // lightweight custom DOM event.
  useEffect(() => {
    const handler = () => { refetchRef.current() }
    document.addEventListener('community:playlist-updated', handler)
    return () => document.removeEventListener('community:playlist-updated', handler)
  }, [refetchRef])

  // Refetch on window focus to pick up mutations that happened while the
  // user was interacting with overlays (modals, share sheets) that may not
  // dispatch our custom event.
  const lastFocusRefetchRef = useRef(0)
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const handler = () => {
      const now = Date.now()
      // Debounce: at most once every 5 seconds
      if (now - lastFocusRefetchRef.current > 5000) {
        lastFocusRefetchRef.current = now
        refetchRef.current()
      }
    }
    window.addEventListener('focus', handler)
    return () => window.removeEventListener('focus', handler)
  }, [refetchRef])

  // Per-row hydration means each row owns its own skeleton gate via the
  // `isHydrating` field on its useRowHydration result, so `playlistsLoading`
  // here is just the shell-query loading flag.
  //
  // Mirrors useFollowedPlaylists: under cache-and-network, `shellLoading`
  // also flips true while a background refetch is in flight on a revisit,
  // even though `playlistsRawData` is already populated from cache.
  // Consumers (PlaylistTabContainer's render gate, useCollectionsData's
  // `sharedOnProfileCount` memo, the `myPlaylistsSettled` pre-mount latch)
  // all treat `playlistsLoading` as "we have nothing to show yet" — so
  // gate it on the absence of data so a revisit paints from cache instead
  // of blanking the screen while the network revalidates.
  const playlistsLoading = shellLoading && !playlistsRawData

  return {
    playlistsRawData,
    playlistsLoading,
    refetchPlaylists,
    loadMorePlaylists: loadMore,
    isLoadingMorePlaylists: isLoadingMore,
    allRawPlaylists,
    allUserPlaylistsJS,
    defaultPlaylist,
    favoritesPlaylist,
    watchLaterVisibility,
    handleUpdatePlaylistVisibility,
    handlePlaylistAction,
    handleRemovePlaylistItem,
    handleRemoveWatchLaterItem,
    handleRemoveFavoritesItem,
    handleReorderPlaylistItem,
    handleReorderWatchLaterItem,
    handleReorderFavoritesItem,
    handleHideInProgressItem,
  }
}
